import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import brandRoles from 'utils/brandRoles';

/**
 * Gemeinsame Bausteine der Projektverwaltung (Initiator-Seite).
 * Masse und Farben nach Markenhandbuch: Cards mit Rand statt Schatten,
 * Radius 12 px, Abstaende im Vier-Pixel-Raster.
 */

export const FONT = 'Inter, sans-serif';

export const Section = styled.section`
  margin-top: 24px;
  padding: 24px;
  background: ${brandRoles.flaecheWeiss};
  border: 1px solid ${brandRoles.neutral200};
  border-radius: 12px;
  font-family: ${FONT};

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;

  button {
    margin-bottom: 0;
  }
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-family: ${FONT};
  font-size: 19px;
  font-weight: 600;
  line-height: 1.35;
  color: ${brandRoles.neutral800};

  .count {
    margin-left: 8px;
    font-size: 14px;
    font-weight: 400;
    color: ${brandRoles.neutral500};
  }
`;

// Count badge next to section titles, as on the project page
export const CountPill = styled.span`
  margin-left: 12px;
  padding: 4px 12px;
  border: 1px solid ${brandRoles.randDezent};
  border-radius: 999px;
  background: ${brandRoles.erfolgFlaeche};
  font-family: ${FONT};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${brandRoles.tagHellText};
`;

// Table look of the project management page: quiet header row, lines between rows only
export const tableCss = `
  .ant-table {
    font-family: ${FONT};
    font-size: 14px;
    color: ${brandRoles.neutral800};
  }

  .ant-table-thead > tr > th {
    padding: 10px 12px;
    background: ${brandRoles.neutral50};
    border-bottom: 1px solid ${brandRoles.neutral200};
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${brandRoles.neutral500};
  }

  .ant-table-thead > tr > th::before {
    display: none;
  }

  .ant-table-tbody > tr > td {
    padding: 12px;
    border-bottom: 1px solid ${brandRoles.neutral200};
    vertical-align: top;
    overflow: hidden;
  }

  .ant-table-tbody > tr:hover > td {
    background: ${brandRoles.neutral50};
  }
`;

export const TextButton = styled.button`
  padding: 0;
  border: 0;
  background: none;
  font-family: ${FONT};
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  color: ${brandRoles.linkText};
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: ${brandRoles.linkHover};
  }

  &:disabled {
    color: ${brandRoles.neutral500};
    cursor: default;
  }
`;

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 5px 11px;
  border-radius: 9999px;
  font-family: ${FONT};
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  background: ${({ $tone }) =>
    $tone === 'gruen' ? brandRoles.tagHellFlaeche : brandRoles.neutral100};
  color: ${({ $tone }) =>
    $tone === 'gruen' ? brandRoles.tagHellText : brandRoles.neutral700};
`;

// Linien-Icons, 2 px Strich, runde Enden (Markenhandbuch "Icons").
export function LineIcon({ name, size = 16 }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };
  switch (name) {
    case 'haken':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M8 12.5l2.5 2.5L16 9.5" />
        </svg>
      );
    case 'kreuz':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M9 9l6 6M15 9l-6 6" />
        </svg>
      );
    case 'achtung':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7.5v5.5M12 16.5v.01" />
        </svg>
      );
    case 'info':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 11v5.5M12 7.5v.01" />
        </svg>
      );
    case 'download':
      return (
        <svg {...common}>
          <path d="M12 4v11M7 10.5l5 5 5-5M5 20h14" />
        </svg>
      );
    case 'geschenk':
      return (
        <svg {...common}>
          <rect x="3.5" y="8" width="17" height="4" rx="1" />
          <path d="M5 12v7.5a1 1 0 001 1h12a1 1 0 001-1V12M12 8v12.5" />
          <path d="M12 8C10.5 4.5 7 4.5 7 6.5S10 8 12 8zM12 8c1.5-3.5 5-3.5 5-1.5S14 8 12 8z" />
        </svg>
      );
    case 'zurueck':
      return (
        <svg {...common}>
          <path d="M15 18l-6-6 6-6" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7.5V12l3 2" />
        </svg>
      );
  }
}

LineIcon.propTypes = {
  name: PropTypes.string,
  size: PropTypes.number,
};

const NOTICE_TONES = {
  hinweis: [
    brandRoles.hinweisFlaeche,
    brandRoles.hinweisRand,
    brandRoles.hinweisSymbol,
    brandRoles.hinweisText,
  ],
  erfolg: [
    brandRoles.erfolgFlaeche,
    brandRoles.erfolgRand,
    brandRoles.erfolgSymbol,
    brandRoles.erfolgText,
  ],
  warnung: [
    brandRoles.warnungFlaeche,
    brandRoles.warnungRand,
    brandRoles.warnungSymbol,
    brandRoles.warnungText,
  ],
  fehler: [
    brandRoles.fehlerFlaeche,
    brandRoles.fehlerRand,
    brandRoles.fehlerSymbol,
    brandRoles.fehlerText,
  ],
};

const NOTICE_ICONS = {
  hinweis: 'info',
  erfolg: 'haken',
  warnung: 'achtung',
  fehler: 'kreuz',
};

/** Hinweis nach "Alert" im Markenhandbuch: Icon und Satz, Farbe nie allein. */
export function Notice({ tone = 'hinweis', title, children, onClose }) {
  const [flaeche, rand, symbol, text] = NOTICE_TONES[tone];
  return (
    <NoticeBox
      style={{ background: flaeche, borderColor: rand }}
      role={tone === 'fehler' ? 'alert' : 'status'}
    >
      <span className="notice-icon" style={{ color: symbol }}>
        <LineIcon name={NOTICE_ICONS[tone]} size={20} />
      </span>
      <div className="notice-body">
        <div className="notice-title" style={{ color: text }}>
          {title}
        </div>
        {children && <div className="notice-text">{children}</div>}
      </div>
      {onClose && (
        <button
          type="button"
          className="notice-close"
          onClick={onClose}
          aria-label="Hinweis schließen"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      )}
    </NoticeBox>
  );
}

Notice.propTypes = {
  tone: PropTypes.oneOf(['hinweis', 'erfolg', 'warnung', 'fehler']),
  title: PropTypes.node.isRequired,
  children: PropTypes.node,
  onClose: PropTypes.func,
};

const NoticeBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 16px;
  padding: 12px 16px;
  border: 1px solid;
  border-radius: 8px;
  font-family: ${FONT};

  .notice-icon {
    flex: 0 0 20px;
    display: inline-flex;
  }

  .notice-body {
    flex: 1;
    min-width: 0;
  }

  .notice-title {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
  }

  .notice-text {
    margin-top: 2px;
    font-size: 14px;
    line-height: 21px;
    color: ${brandRoles.neutral700};

    a {
      color: ${brandRoles.linkText};
      text-decoration: underline;
    }
  }

  .notice-close {
    flex: 0 0 auto;
    padding: 2px;
    border: 0;
    background: none;
    color: ${brandRoles.neutral500};
    cursor: pointer;
    display: inline-flex;

    &:hover {
      color: ${brandRoles.neutral800};
    }
  }
`;

/**
 * Seiten-Navigation nach dem Vorbild des Profil-Redesigns ("Unterstuetzungen"):
 * "Seite x von y", Zurueck/Weiter, Seitenzahlen nur ab Tablet.
 */
export function Pagination({ page, pages, onChange }) {
  if (!pages || pages <= 1) return null;

  const go = (p) => {
    const next = Math.min(Math.max(1, p), pages);
    if (next !== page) onChange(next);
  };

  return (
    <PaginationWrap aria-label="Seiten">
      <span className="info">
        Seite {page} von {pages}
      </span>
      <div className="controls">
        <button
          type="button"
          className="nav"
          onClick={() => go(page - 1)}
          disabled={page <= 1}
        >
          <Chevron dir="left" />
          Zurück
        </button>
        <div className="pages">
          {pageList(page, pages).map((p, i) =>
            p === '…' ? (
              <span key={`gap-${i}`} className="gap">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className={p === page ? 'page active' : 'page'}
                aria-current={p === page ? 'page' : undefined}
                onClick={() => go(p)}
              >
                {p}
              </button>
            )
          )}
        </div>
        <button
          type="button"
          className="nav"
          onClick={() => go(page + 1)}
          disabled={page >= pages}
        >
          Weiter
          <Chevron dir="right" />
        </button>
      </div>
    </PaginationWrap>
  );
}

Pagination.propTypes = {
  page: PropTypes.number.isRequired,
  pages: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

function pageList(page, pages) {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  if (page <= 4) return [1, 2, 3, 4, 5, '…', pages];
  if (page >= pages - 3)
    return [1, '…', pages - 4, pages - 3, pages - 2, pages - 1, pages];
  return [1, '…', page - 1, page, page + 1, '…', pages];
}

function Chevron({ dir }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={dir === 'left' ? 'M10 12L6 8l4-4' : 'M6 12l4-4-4-4'} />
    </svg>
  );
}

Chevron.propTypes = { dir: PropTypes.oneOf(['left', 'right']) };

const PaginationWrap = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 16px;
  font-family: ${FONT};

  .info {
    font-size: 14px;
    line-height: 21px;
    color: ${brandRoles.neutral500};
  }

  .controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .nav {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 7px 12px;
    border: 1px solid ${brandRoles.neutral200};
    border-radius: 8px;
    background: ${brandRoles.flaecheWeiss};
    font-family: ${FONT};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: ${brandRoles.neutral700};
    cursor: pointer;

    &:hover:not(:disabled) {
      background: ${brandRoles.neutral50};
    }

    &:disabled {
      color: ${brandRoles.neutral300};
      cursor: default;
    }
  }

  .pages {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  .page {
    min-width: 32px;
    height: 32px;
    padding: 0 6px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    font-family: ${FONT};
    font-size: 14px;
    font-weight: 500;
    color: ${brandRoles.neutral700};
    cursor: pointer;

    &:hover {
      background: ${brandRoles.neutral100};
    }

    &.active {
      background: ${brandRoles.neutral100};
      color: ${brandRoles.neutral800};
      font-weight: 600;
    }
  }

  .gap {
    padding: 0 4px;
    color: ${brandRoles.neutral500};
  }

  @media (max-width: 767px) {
    .pages {
      display: none;
    }
  }
`;
